import { Link } from 'react-router-dom';
import { formatPlainDate } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalCaseListItem } from '../../core/models/portal-case-list-item';
import { Badge } from '../../ui/badge';
import { ArrowRightIcon, ClockIcon } from '../../ui/portal-icons';

/**
 * One case in the portal's list.
 *
 * **The whole row is the link.** It used to be a card with a small "Open" anchor in the corner,
 * which on a telephone is a five-millimetre target under the one thing a customer actually wants to
 * tap. The stage is still the accessible name of that link, so nothing is lost to a screen reader
 * by the rest of the row being clickable.
 *
 * A case sitting on a step with no customer-visible text shows **no stage at all** rather than an
 * invented one — the server sends null, and filling that in from anywhere would be the disclosure
 * the projection exists to prevent.
 */
export function CaseCard({ clientCase }: { clientCase: PortalCaseListItem }) {
    const { t, language } = useTranslation();

    const milestone = language === 'kk' ? clientCase.milestoneKk : clientCase.milestoneRu;

    return (
        <li>
            <Link
                to={`/${language}/portal/cases/${clientCase.id}`}
                className="group flex gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md hover:shadow-brand-900/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand-700"
            >
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                            {clientCase.caseNumber}
                        </span>
                        <Badge tone={clientCase.isClosed ? 'neutral' : 'brand'}>
                            {clientCase.isClosed ? t('portal.closed') : t('portal.in_progress')}
                        </Badge>
                    </div>

                    <p className="mt-2 font-bold text-slate-900 group-hover:text-brand-700 dark:text-slate-100 dark:group-hover:text-brand-300">
                        {milestone ?? t('portal.no_stage')}
                    </p>

                    {clientCase.nextActionText !== null && (
                        <p className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                            <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {t('portal.next_action')}:
                            </span>
                            <span>{clientCase.nextActionText}</span>

                            {clientCase.nextActionDueOn !== null && (
                                <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                                    <ClockIcon className="size-3.5" />
                                    {t('portal.due')} {formatPlainDate(clientCase.nextActionDueOn, language)}
                                </span>
                            )}
                        </p>
                    )}
                </div>

                <ArrowRightIcon className="size-5 shrink-0 self-center text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-brand-600 dark:text-slate-600 dark:group-hover:text-brand-400" />
            </Link>
        </li>
    );
}
